import { Layout, Model, TabNode, IJsonModel, Actions } from "flexlayout-react";
import { useCallback, useRef, useMemo } from "react";
import { Box } from "@mantine/core";
import { WidgetContainer } from "./WidgetContainer";
import { getWidget } from "./registry";
import { WidgetDefinition, GroupId } from "@pulse-ui/widget-sdk";
import "flexlayout-react/style/light.css";

const WIDGET_DRAG_MIME_TYPE = "application/x-atlas-widget";

interface WorkspaceLayoutProps {
  /** FlexLayout model (JSON or Model instance) */
  model: IJsonModel | Model;

  /** Callback when model changes */
  onModelChange?: (model: Model) => void;

  /** Custom widget registry - apps should provide their own registry */
  widgetRegistry?: Record<string, WidgetDefinition>;
}

/**
 * Main workspace layout container using FlexLayout
 * Handles widget rendering, drag-drop, and layout persistence
 */
export function WorkspaceLayout({
  model: modelProp,
  onModelChange,
  widgetRegistry,
}: WorkspaceLayoutProps) {
  const layoutRef = useRef<Layout>(null);

  // Convert JSON model to Model instance if needed
  const model = useMemo(() => {
    if (modelProp instanceof Model) {
      return modelProp;
    }
    return Model.fromJson(modelProp);
  }, [modelProp]);

  /**
   * Factory function to render widget content for each tab
   */
  const factory = useCallback(
    (node: TabNode) => {
      const widgetId = node.getComponent();
      const instanceId = node.getId();
      const config = node.getConfig();
      const groupId = config?.groupId as GroupId | undefined;

      if (!widgetId) {
        return <div>No component specified</div>;
      }

      // Use passed-in registry first, fall back to singleton
      const widgetDef = widgetRegistry?.[widgetId] ?? getWidget(widgetId);
      if (!widgetDef) {
        return <div>Widget not found: {widgetId}</div>;
      }

      return (
        <WidgetContainer
          widgetId={widgetId}
          instanceId={instanceId}
          WidgetComponent={widgetDef.component}
          config={config}
          groupId={groupId}
        />
      );
    },
    [widgetRegistry],
  );

  /**
   * Handle model changes (drag-drop, resize, etc.)
   */
  const handleModelChange = useCallback(
    (newModel: Model) => {
      if (onModelChange) {
        onModelChange(newModel);
      }
    },
    [onModelChange],
  );

  /**
   * Handle external drag (from widget library)
   */
  const handleExternalDrag = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      // Validate it's our widget
      if (!e.dataTransfer?.types.includes(WIDGET_DRAG_MIME_TYPE)) {
        return;
      }

      return {
        json: { type: "tab" },
        onDrop: (node?: any, event?: React.DragEvent<HTMLElement>) => {
          if (!node || !event || !event.dataTransfer) return;

          const widgetId = event.dataTransfer.getData(WIDGET_DRAG_MIME_TYPE);
          const widget = widgetRegistry?.[widgetId] ?? getWidget(widgetId);

          if (!widget) return;

          // Update the dropped node with widget info
          node.getModel().doAction(
            Actions.updateNodeAttributes(node.getId(), {
              name: widget.name,
              component: widgetId,
              config: { groupId: 'default', ...(widget.config || {}) },
            }),
          );
        },
      };
    },
    [widgetRegistry],
  );

  return (
    <Box h="100%" w="100%" style={{ position: "relative" }}>
      <Layout
        ref={layoutRef}
        model={model}
        factory={factory}
        onModelChange={handleModelChange}
        onExternalDrag={handleExternalDrag}
      />
    </Box>
  );
}
